import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import organizationService from "~/modules/organization/services/organization.service";
import superAdministrationService from "~/modules/superAdministration/services/superAdministration.service";
import type { User } from "~/modules/user/models/User.interface";
import type { UserCreateDto } from "~/modules/user/models/UserCreateDto.interface";
import type { UserDetails } from "~/modules/user/models/UserDetails.interface";
import type { UserUpdateDto } from "~/modules/user/models/UserUpdateDto.interface";

export class UserAdministrationService extends RestService {
  protected USERS = "users";
  protected UNASSIGNED_USERS = "unassignedUsers";
  protected ACTIVATE = "activate";
  protected DEACTIVATE = "deactivate";

  async getSuperAdministrationUsers({
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<User>> {
    return this.$apiV2.get(this.getSuperAdministrationUsersPath(), {
      params: { page, pageSize, filter: searchParams, sort, ...params },
    });
  }

  async getBusinessUnitUsers({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<User>> {
    return this.$apiV2.get(this.getBusinessUnitUsersPath(businessUnitCode), {
      params: { page, pageSize, filter: searchParams, sort, ...params },
    });
  }

  async getOrganizationUsers({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<User>> {
    return this.$apiV2.get(this.getOrganizationUsersPath(organizationCode), {
      params: { page, pageSize, filter: searchParams, sort, ...params },
    });
  }

  async getOrganizationSectorUsers({
    organizationCode,
    sectorCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<User>> {
    return this.$apiV2.get(
      this.getOrganizationSectorPath(organizationCode, sectorCode),
      {
        params: { page, pageSize, filter: searchParams, sort, ...params },
      },
    );
  }

  async getOrganizationSectorUnassignedUsers({
    organizationCode,
    sectorCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<User>> {
    return this.$apiV2.get(
      this.getOrganizationSectorPath(organizationCode, sectorCode, true),
      {
        params: { page, pageSize, filter: searchParams, sort, ...params },
      },
    );
  }

  createSuperAdministrationUser({
    user,
  }: GenericParameters & { user: UserCreateDto }) {
    return this.$apiV2.post(this.getSuperAdministrationUsersPath(), user);
  }

  createBusinessUnitUser({
    businessUnitCode,
    user,
  }: GenericParameters & { user: UserCreateDto }) {
    return this.$apiV2.post(
      this.getBusinessUnitUsersPath(businessUnitCode),
      user,
    );
  }

  createOrganizationUser({
    organizationCode,
    user,
  }: GenericParameters & { user: UserCreateDto }) {
    return this.$apiV2.post(
      this.getOrganizationUsersPath(organizationCode),
      user,
    );
  }

  /**
   * Delete super administration user
   * @param userLogin
   */
  async deleteSuperAdministrationUser({
    userLogin,
  }: GenericParameters & {
    userLogin: string;
  }): Promise<void> {
    return this.$apiV2.delete(
      `${this.getSuperAdministrationUsersPath()}/${userLogin}`,
    );
  }

  /**
   * Delete business unit user
   * @param businessUnitCode
   * @param userLogin
   */
  async deleteBusinessUnitUser({
    businessUnitCode,
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<void> {
    return this.$apiV2.delete(
      `${this.getBusinessUnitUsersPath(businessUnitCode)}/${userLogin}`,
    );
  }

  /**
   * Delete organization user
   * @param organizationCode
   * @param userLogin
   */
  async deleteOrganizationUser({
    organizationCode,
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<void> {
    return this.$apiV2.delete(
      `${this.getOrganizationUsersPath(organizationCode)}/${userLogin}`,
    );
  }

  /**
   * Activate super administration user
   * @param userLogin
   */
  async activateSuperAdministrationUser(userLogin: string): Promise<User> {
    return this.$apiV2.patch(
      `${this.getSuperAdministrationUsersPath()}/${userLogin}/${this.ACTIVATE}`,
    );
  }

  /**
   * Deactivate super administration user
   * @param userLogin
   */
  async deactivateSuperAdministrationUser({
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<User> {
    return this.$apiV2.patch(
      `${this.getSuperAdministrationUsersPath()}/${userLogin}/${this.DEACTIVATE}`,
    );
  }

  /**
   * Activate business unit user
   * @param businessUnitCode
   * @param userLogin
   */
  async activateBusinessUnitUser({
    businessUnitCode,
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<User> {
    return this.$apiV2.patch(
      `${this.getBusinessUnitUsersPath(businessUnitCode)}/${userLogin}/${this.ACTIVATE}`,
    );
  }

  /**
   * Deactivate business unit user
   * @param businessUnitCode
   * @param userLogin
   */
  async deactivateBusinessUnitUser({
    businessUnitCode,
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<User> {
    return this.$apiV2.patch(
      `${this.getBusinessUnitUsersPath(businessUnitCode)}/${userLogin}/${this.DEACTIVATE}`,
    );
  }

  /**
   * Activate organization user
   * @param organizationCode
   * @param userLogin
   */
  async activateOrganizationUser({
    organizationCode,
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<User> {
    return this.$apiV2.patch(
      `${this.getOrganizationUsersPath(organizationCode)}/${userLogin}/${this.ACTIVATE}`,
    );
  }

  /**
   * Deactivate organization user
   * @param organizationCode
   * @param userLogin
   */
  async deactivateOrganizationUser({
    organizationCode,
    userLogin,
  }: GenericParameters & { userLogin: string }): Promise<User> {
    return this.$apiV2.patch(
      `${this.getOrganizationUsersPath(organizationCode)}/${userLogin}/${this.DEACTIVATE}`,
    );
  }

  updateSuperAdministrationUser({
    userLogin,
    user,
  }: GenericParameters & {
    userLogin: string;
    user: UserUpdateDto;
  }) {
    return this.$apiV2.put(
      `${this.getSuperAdministrationUsersPath()}/${userLogin}`,
      user,
    );
  }

  updateBusinessUnitUser({
    businessUnitCode,
    userLogin,
    user,
  }: GenericParameters & { userLogin: string; user: UserUpdateDto }) {
    return this.$apiV2.put(
      `${this.getBusinessUnitUsersPath(businessUnitCode)}/${userLogin}`,
      user,
    );
  }

  updateOrganizationUser({
    organizationCode,
    userLogin,
    user,
  }: GenericParameters & { userLogin: string; user: UserUpdateDto }) {
    return this.$apiV2.put(
      `${this.getOrganizationUsersPath(organizationCode)}/${userLogin}`,
      user,
    );
  }

  async getSuperAdministrationUser({
    userLogin,
    params,
  }: GenericParameters & {
    userLogin: string;
    params: Map<string, unknown>;
  }): Promise<UserDetails> {
    return this.$apiV2.get(
      `${this.getSuperAdministrationUsersPath()}/${userLogin}`,
      {
        params,
      },
    );
  }

  async getBusinessUnitUser({
    businessUnitCode,
    userLogin,
    params,
  }: GenericParameters & {
    userLogin: string;
    params: Map<string, unknown>;
  }): Promise<UserDetails> {
    return this.$apiV2.get(
      `${this.getBusinessUnitUsersPath(businessUnitCode)}/${userLogin}`,
      {
        params,
      },
    );
  }

  async getOrganizationUser({
    organizationCode,
    userLogin,
    params,
  }: GenericParameters & {
    userLogin: string;
    params: Map<string, unknown>;
  }): Promise<UserDetails> {
    return this.$apiV2.get(
      `${this.getOrganizationUsersPath(organizationCode)}/${userLogin}`,
      {
        params,
      },
    );
  }

  private getSuperAdministrationUsersPath() {
    return `${superAdministrationService.getSuperAdministrationPath()}/${this.USERS}`;
  }

  private getBusinessUnitUsersPath(businessUnitCode?: number) {
    return `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.USERS}`;
  }

  private getOrganizationUsersPath(organizationCode?: number) {
    return `${organizationService.getOrganizationPath(organizationCode)}/${this.USERS}`;
  }

  private getOrganizationSectorPath(
    organizationCode?: number,
    sectorCode?: number,
    unassignedUsers?: boolean,
  ) {
    return `${organizationService.getOrganizationPath(organizationCode)}/sectors/${sectorCode}/${unassignedUsers ? this.UNASSIGNED_USERS : this.USERS}`;
  }
}
export default new UserAdministrationService();
